import { useState, useRef, useEffect } from 'react';
import { Radio, Check, ChevronDown } from 'lucide-react';
import type { Provider } from '@lib/streamFlowApi';

interface MirrorSwitcherProps {
  providers: Provider[];
  /** Provider ids in priority order (priority 0 first). */
  availableProviderIds: string[];
  /** Provider currently serving the player. */
  currentProviderId: string;
  /** Switch to a specific provider id. The parent re-mints the URL. */
  onSwitch: (providerId: string) => void;
}

/**
 * Manual mirror switcher. Renders a "Source N of M ▾" pill that opens a
 * dropdown listing every provider carrying the channel; clicking one
 * forces the player to re-mint via that provider's source. Pairs with
 * the auto-failover loop in StreamPlayer for the typical "first mirror
 * died" recovery.
 */
export default function MirrorSwitcher({ providers, availableProviderIds, currentProviderId, onSwitch }: MirrorSwitcherProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  // Close on outside click for the dropdown.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const total = availableProviderIds.length;
  const current = availableProviderIds.indexOf(currentProviderId);
  const positionLabel = current >= 0 ? `Source ${current + 1} of ${total}` : `${total} mirrors`;

  const providerName = (id: string): string => providers.find(p => p.id === id)?.name ?? id.slice(0, 8);

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 bg-white hover:bg-gray-50"
      >
        <Radio className="w-3.5 h-3.5 text-blue-600" />
        {positionLabel}
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute mt-1 left-0 w-72 bg-white border rounded-lg shadow-lg z-20 overflow-hidden">
          <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500 border-b bg-gray-50">
            Mirrors (priority order)
          </div>
          <div className="max-h-64 overflow-y-auto">
            {availableProviderIds.map((pid, idx) => {
              const isCurrent = pid === currentProviderId;
              return (
                <button
                  key={pid}
                  type="button"
                  onClick={() => {
                    if (!isCurrent) onSwitch(pid);
                    setOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 hover:bg-gray-50 ${
                    isCurrent ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="truncate font-medium">{providerName(pid)}</p>
                    <p className="text-[10px] text-gray-500">priority {idx}</p>
                  </div>
                  {isCurrent && <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
